import { isNil } from '@flowio/is';
import fetchAuthorization from '../../services/authorization/fetchAuthorization';
import isAuthorizationApproved from '../../utilities/isAuthorizationApproved';
import loadScript from '../../utilities/loadScript';
import pollForAuthorizationUpdate from '../../utilities/pollForAuthorizationUpdate';
import { showLoadingSpinner, hideLoadingSpinner } from '../../utilities/loadingSpinner';

const STRIPE_JAVASCRIPT = 'https://js.stripe.com/v3/';

type HandleOgStripeActionParams = {
  authActionDetails: io.flow.v0.models.StripeAuthorizationResultActionDetails,
  authKey: string;
  organizationId: string,
  sessionId: string,
};

export default function handleOgStripeAction({
  authActionDetails,
  authKey,
  organizationId,
  sessionId,
}: HandleOgStripeActionParams): Promise<io.flow.v0.models.CardAuthorization> {
  if (isNil(authActionDetails.publishable_key) || isNil(authActionDetails.client_secret)) {
    throw new Error('handleOgStripeAction: method inappropriately invoked.');
  }
  showLoadingSpinner();
  return new Promise<io.flow.v0.models.CardAuthorization>((resolve, reject) => {
    loadScript(STRIPE_JAVASCRIPT).then(() => {
      const stripe = Stripe(authActionDetails.publishable_key!);
      hideLoadingSpinner();
      return stripe.confirmCardPayment(authActionDetails.client_secret!, {
        payment_method: authActionDetails.payment_method_id,
      });
    }).then((stripeResult) => {
      showLoadingSpinner();
      if (stripeResult.error) {
        reject(stripeResult.error);
      } else if (stripeResult.paymentIntent) {
        fetchAuthorization({
          key: authKey,
          organizationId,
          sessionId,
        }).then((auth) => {
          if (!isAuthorizationApproved(auth)) {
            return new Promise<io.flow.v0.unions.Authorization>((pollResolve, pollReject) => {
              pollForAuthorizationUpdate(
                {
                  auth,
                  organizationId,
                  sessionId,
                  diffCheck: (_, auth2) => isAuthorizationApproved(auth2),
                  onChange: (updatedAuth) => pollResolve(updatedAuth),
                  onTimeout: pollReject,
                  startInterval: 1,
                  timeoutInterval: 10,
                },
              );
            });
          }
          return auth;
        }).then((approvedAuth) => {
          if (approvedAuth.discriminator === 'card_authorization') {
            resolve(approvedAuth);
            hideLoadingSpinner();
            return;
          }
          throw new Error('@flowio/payment-sdk: Unexpected non CardAuthorization returned.');
        });
      } else {
        throw new Error('@flowio/payment-sdk: Stripe confirmCardPayment returned undefined paymentIntent.');
      }
    });
  });
}
